import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  LayoutDashboard, Truck, History, DollarSign, ListFilter, Plus, ArrowUp, 
  Users, Car, Bell, Settings, TrendingUp, Activity, Award, AlertCircle,
  CheckCircle, Clock, Package, FileText, AlertTriangle, Target
} from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { useTransporterStats } from '@/hooks/useTransporterStats';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import LogiConnectIdBadge from '@/components/ui/logiconnect-id-badge';
import useAuthStore from '@/store/authStore';
import AdvancedChart from '@/components/charts/AdvancedChart';
import { ResponsiveContainer, PieChart, Pie, Cell, BarChart as RechartsBarChart, XAxis, YAxis, Tooltip, Legend, Bar } from 'recharts';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useTheme } from 'next-themes';
import Divider from '@/components/ui/divider';
import { getTransporterSidebarLinks } from '@/config/transporterSidebarLinks';

const StatCard = ({ title, value, change, changeType, icon: Icon, iconColor, children }) => (
  <Card className="bg-card border border-border rounded-xl p-6 hover:border-secondary transition-colors">
    <div className="flex justify-between items-start mb-2 min-h-[20px]">
      <p className="text-sm text-muted-foreground flex-1">{title}</p>
      {Icon && <Icon className={cn("w-5 h-5 flex-shrink-0", iconColor || "text-blue-500")} />}
    </div>
    <p className="text-3xl lg:text-4xl font-bold text-foreground mb-1">{value}</p>
    {change && (
      <div className={cn("flex items-center text-sm", changeType === 'positive' ? 'text-green-500' : 'text-red-500')}>
        {changeType === 'positive' ? <ArrowUp className="w-4 h-4 mr-1" /> : <ArrowUp className="w-4 h-4 mr-1 rotate-180" />}
        <span>{change}</span>
      </div>
    )}
    {children}
  </Card>
);

const DetailStatCard = ({ title, value, subValue1, subValue2, icon: Icon, iconColor, children }) => (
  <Card className="bg-card border border-border rounded-xl p-6 flex flex-col justify-between hover:border-secondary transition-colors">
    <div>
      <div className="flex justify-between items-start mb-2 min-h-[20px]">
        <p className="text-sm text-muted-foreground flex-1">{title}</p>
        {Icon && <Icon className={cn("w-5 h-5 flex-shrink-0", iconColor || "text-blue-500")} />}
      </div>
      <p className="text-3xl font-bold text-foreground mb-2">{value}</p>
    </div>
    <div>
      <p className="text-sm text-foreground/80">{subValue1}</p>
      <p className="text-sm text-muted-foreground">{subValue2}</p>
    </div>
    {children}
  </Card>
);

const TransporterDashboard = () => {
  const { theme } = useTheme();
  const { stats, loading, error } = useTransporterStats();
  const { user } = useAuthStore();
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  
  const sidebarLinks = getTransporterSidebarLinks(stats);

  // Formatage des montants
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '0 GNF';
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M GNF`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(0)}K GNF`;
    }
    return `${amount?.toFixed(0) || 0} GNF`;
  };

  // Génération de données adaptées à la période sélectionnée
  const generateChartData = useMemo(() => {
    const generateDailyData = (days) => {
      const data = [];
      const today = new Date();
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(today.getDate() - i);
        const dayLabel = date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
        
        // Simulation de données avec tendance croissante + variation
        const baseRevenue = (stats.revenus_mois || 0) / 30 + Math.random() * (stats.revenus_mois || 100000) / 50;
        const baseMissions = (stats.missions_en_cours || 0) / 30 + Math.random() * 2;
        
        data.push({
          name: dayLabel,
          revenus: Number((baseRevenue + Math.random() * baseRevenue * 0.3).toFixed(0)),
          missions: Math.round(baseMissions + Math.random() * 3),
        });
      }
      return data;
    };

    const monthlyData = [
      { name: 'Jan', revenus: stats.revenus_mois * 0.2 || 0, missions: 5 },
      { name: 'Fév', revenus: stats.revenus_mois * 0.3 || 0, missions: 8 },
      { name: 'Mar', revenus: stats.revenus_mois * 0.4 || 0, missions: 12 },
      { name: 'Avr', revenus: stats.revenus_mois * 0.5 || 0, missions: 15 },
      { name: 'Mai', revenus: stats.revenus_mois * 0.7 || 0, missions: 18 },
      { name: 'Juin', revenus: stats.revenus_mois * 0.9 || 0, missions: 20 },
      { name: 'Juil', revenus: stats.revenus_mois || 0, missions: (stats.missions_en_cours || 0) + 5 },
      { name: 'Août', revenus: stats.revenus_mois * 1.1 || 0, missions: (stats.missions_en_cours || 0) + 8 },
      { name: 'Sep', revenus: stats.revenus_mois * 1.2 || 0, missions: (stats.missions_en_cours || 0) + 10 },
      { name: 'Oct', revenus: stats.revenus_mois * 1.3 || 0, missions: (stats.missions_en_cours || 0) + 12 },
      { name: 'Nov', revenus: stats.revenus_mois * 1.4 || 0, missions: (stats.missions_en_cours || 0) + 15 },
      { name: 'Déc', revenus: stats.revenus_mois * 1.5 || 0, missions: (stats.missions_en_cours || 0) + 18 },
    ];

    switch (selectedPeriod) {
      case '7d':
        return generateDailyData(7);
      case '30d':
        return generateDailyData(30);
      case '3m':
        return monthlyData.slice(-3);
      case '6m':
        return monthlyData.slice(-6);
      case '1y':
        return monthlyData;
      case 'all':
      default:
        return monthlyData;
    }
  }, [selectedPeriod, stats]);

  // Configuration des séries pour le graphique avancé
  const chartSeries = [
    { key: 'revenus', name: 'Revenus', color: '#E3B23C', type: 'line' },
    { key: 'missions', name: 'Missions', color: '#10b981', type: 'bar' },
  ];

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  // Activités récentes (à remplacer par les vraies données)
  const recentActivities = [
    { 
      type: 'mission', 
      text: 'Nouvelle mission confirmée - Conakry → Kindia', 
      time: 'il y a 2h', 
      icon: Truck, 
      link: '/transporter/missions-requests',
      color: 'text-green-500'
    },
    { 
      type: 'request', 
      text: 'Nouvelle demande disponible - Transport de marchandises lourdes', 
      time: 'il y a 5h', 
      icon: Package, 
      link: '/transporter/missions-requests',
      color: 'text-blue-500'
    },
    { 
      type: 'payment', 
      text: 'Paiement reçu - Mission #BK-1234 - 1.5M GNF', 
      time: 'il y a 1 jour', 
      icon: DollarSign, 
      link: '/transporter/earnings',
      color: 'text-yellow-500'
    },
    { 
      type: 'vehicle', 
      text: 'Assurance du véhicule AB-123-CD expire dans 15 jours', 
      time: 'il y a 2 jours', 
      icon: AlertTriangle, 
      link: '/transporter/vehicles',
      color: 'text-orange-500'
    },
  ];

  const missionStatusData = [
    { name: 'Terminées', value: stats.missions_terminees || 0 },
    { name: 'En cours', value: stats.missions_en_cours || 0 },
    { name: 'En attente', value: stats.nouvelles_demandes || 0 },
  ];
  const PIE_COLORS = ['#10b981', '#3b82f6', '#f59e0b'];

  const tooltipStyle = {
    backgroundColor: theme === 'dark' ? '#021f17' : 'hsl(var(--background))',
    border: '1px solid hsl(var(--border))',
    color: 'hsl(var(--foreground))'
  };

  // Calcul du pourcentage d'objectif
  const progressPercentage = stats.pourcentage_objectif || 0;

  return (
    <>
      <Helmet>
        <title>Tableau de bord Transporteur - LogiConnect</title>
      </Helmet>
      <DashboardLayout sidebarLinks={sidebarLinks}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Tableau de bord Transporteur</h1>
            {(user?.logiconnect_id || user?.profile_data?.logiconnect_id) && (
              <div className="mt-2">
                <LogiConnectIdBadge 
                  id={user?.profile_data?.logiconnect_id || user?.logiconnect_id} 
                  showLabel={true}
                  size="sm"
                />
              </div>
            )}
          </div>
          <Button asChild className="gradient-secondary text-primary font-semibold w-full sm:w-auto">
            <Link to="/transporter/missions-requests">
            <Plus className="w-5 h-5 mr-2" />
              Nouvelle offre
            </Link>
          </Button>
        </div>

        {/* KPIs Principaux */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-muted-foreground mb-4">KPIs Principaux</h2>
          <div className="lg:grid lg:grid-cols-4 lg:gap-6">
            <div className="flex space-x-6 overflow-x-auto pb-4 -mx-4 px-4 lg:contents" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <StatCard 
                title="Revenus du mois" 
                value={loading ? '...' : formatCurrency(stats.revenus_mois)} 
                change="+12% vs mois" 
                changeType="positive"
                icon={DollarSign}
                iconColor="text-green-500"
              />
              <StatCard 
                title="Balance actuelle" 
                value={loading ? '...' : formatCurrency(stats.balance)} 
                icon={Activity}
                iconColor="text-blue-500"
              />
              <StatCard 
                title="Missions terminées" 
                value={loading ? '...' : stats.missions_terminees || 0}
                icon={CheckCircle}
                iconColor="text-green-500"
              />
              <StatCard 
                title="Note moyenne" 
                value={loading ? '...' : `${parseFloat(stats.note_moyenne || 0).toFixed(1)}/5`}
                icon={Award}
                iconColor="text-yellow-500"
              />
            </div>
          </div>
          </div>

        {/* Divider élégant */}
        <Divider variant="gradient" icon={TrendingUp} label="Métriques détaillées" />

        {/* Métriques détaillées */}
        <div className="mb-8">
          <div className="lg:grid lg:grid-cols-4 lg:gap-6">
            <div className="flex space-x-6 overflow-x-auto pb-4 -mx-4 px-4 lg:contents" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <DetailStatCard 
                title="Missions en cours" 
                value={loading ? '...' : stats.missions_en_cours || 0} 
                subValue1={`${stats.nouvelles_demandes || 0} nouvelles demandes`} 
                subValue2="Dernière mission il y a 2h"
                icon={Truck}
                iconColor="text-blue-500"
              />
              <DetailStatCard 
                title="Véhicules actifs" 
                value={loading ? '...' : stats.vehicules_actifs || 0} 
                subValue1="Véhicules disponibles" 
                subValue2="Aucun en maintenance"
                icon={Car}
                iconColor="text-purple-500"
              />
              <DetailStatCard 
                title="Chauffeurs" 
                value="N/A" 
                subValue1="Chauffeurs actifs" 
                subValue2="Tous assignés"
                icon={Users}
                iconColor="text-indigo-500"
              />
              <DetailStatCard 
                title="Objectif revenus" 
                value={loading ? '...' : `${Math.round(progressPercentage)}%`} 
                subValue1={formatCurrency(stats.revenus_mois || 0)} 
                subValue2={`sur ${formatCurrency(stats.objectif_revenus || 0)}`}
                icon={Target}
                iconColor="text-green-500"
              >
                <div className="mt-2">
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-secondary h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                    />
                  </div>
                </div>
              </DetailStatCard>
            </div>
          </div>
        </div>

        {/* Graphiques et activités */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">
          <div className="lg:col-span-3">
            <AdvancedChart
              data={generateChartData}
              series={chartSeries}
              title="Évolution des revenus et missions"
              defaultPeriod="all"
              defaultChartType="composed"
              onPeriodChange={handlePeriodChange}
              chartConfig={{ height: 350 }}
            />
          </div>
          <div className="lg:col-span-2">
            <Card className="bg-card border border-border rounded-xl h-full">
              <div className="p-6 border-b border-border">
                <h2 className="text-xl font-semibold text-foreground">Activités récentes</h2>
              </div>
              <div className="p-6 space-y-4 max-h-[400px] overflow-y-auto">
                {recentActivities.map((activity, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className={cn("w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center", 
                      activity.color?.replace('text-', 'bg-') + '/10'
                    )}>
                      <activity.icon className={cn("w-5 h-5", activity.color)} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground">{activity.text}</p>
                      <p className="text-xs text-muted-foreground">{activity.time}</p>
                    </div>
                    <Button asChild variant="ghost" size="sm" className="text-secondary hover:text-secondary hover:bg-secondary/10">
                      <Link to={activity.link}>Voir</Link>
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>

        {/* Graphiques de statut */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-card border border-border rounded-xl">
            <div className="p-6 border-b border-border flex justify-between items-center">
              <h2 className="text-xl font-semibold text-foreground">Statut des Missions</h2>
              <FileText className="w-5 h-5 text-muted-foreground" />
            </div>
            <div className="p-6 h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie 
                    data={missionStatusData} 
                    cx="50%" 
                    cy="50%" 
                    labelLine={false} 
                    outerRadius={100} 
                    fill="#8884d8" 
                    dataKey="value" 
                    nameKey="name" 
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {missionStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle}/>
                  <Legend wrapperStyle={{fontSize: "12px"}}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card className="bg-card border border-border rounded-xl">
            <div className="p-6 border-b border-border flex justify-between items-center">
              <h2 className="text-xl font-semibold text-foreground">Revenus par période</h2>
              <DollarSign className="w-5 h-5 text-muted-foreground" />
            </div>
            <div className="p-6 h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsBarChart data={generateChartData.slice(-6)}>
                  <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={tooltipStyle}/>
                  <Bar dataKey="revenus" fill="#E3B23C" radius={[8, 8, 0, 0]}>
                    {generateChartData.slice(-6).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill="#E3B23C" />
                    ))}
                  </Bar>
                </RechartsBarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      </DashboardLayout>
    </>
  );
};

export default TransporterDashboard;